import { useEffect, useState } from "react";
import { content } from "@/assets/content/logo/content";
import { CornPatternBackground } from "@/components/common/CornPatternBackground";
import { cn } from "@/lib/utils";

interface LoadingScreenProps {
  /** Si es true, se comporta como fallback estático sin temporizador */
  staticFallback?: boolean;
}

/**
 * Obtiene la configuración de la pantalla de carga desde las variables de entorno de Vite.
 * - Si no está definida o está vacía: habilitada con 1 segundo de retardo.
 * - Si es 'false' o '0': deshabilitada por completo.
 * - Si es un número (ms): se usa como tiempo de retardo mínimo.
 */
function getLoadingConfig() {
  const envVal = import.meta.env.VITE_LOADING_SCREEN;

  if (envVal === "false" || envVal === "0") {
    return { enabled: false, minDuration: 0 };
  }

  const numericVal = Number(envVal);
  const minDuration = !isNaN(numericVal) && numericVal > 0 ? numericVal : 1000;

  return { enabled: true, minDuration };
}

export function LoadingScreen({ staticFallback = false }: LoadingScreenProps) {
  const { enabled, minDuration } = getLoadingConfig();

  const [isVisible, setIsVisible] = useState(enabled || staticFallback);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (staticFallback) return;
    if (!enabled) {
      setIsVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsExiting(true);
      const exitTimer = setTimeout(() => {
        setIsVisible(false);
      }, 400); // Duración de la transición de fade-out

      return () => clearTimeout(exitTimer);
    }, minDuration);

    return () => clearTimeout(timer);
  }, [enabled, minDuration, staticFallback]);

  if (!isVisible && !staticFallback) {
    return null;
  }

  return (
    <aside
      aria-label="Cargando sitio"
      aria-live="polite"
      className={cn(
        "fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-background select-none overflow-hidden transition-opacity duration-400 ease-out",
        isExiting ? "opacity-0 pointer-events-none" : "opacity-100"
      )}
    >
      {/* Patrón de choclos grises idéntico al del Hero */}
      <CornPatternBackground
        color="rgba(150, 150, 150, 0.18)"
        gap={60}
        size={14}
      />

      {/* Contenedor central con logo y loader */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4 text-center">
        <img
          src={content.horizontalLogo.src}
          alt={content.horizontalLogo.alt}
          className="h-20 w-auto max-w-[320px] object-contain sm:h-28 md:h-36 sm:max-w-[480px] md:max-w-[560px] drop-shadow-sm"
        />

        {/* Loading spinner amarillo (#EFBC11 / accent) */}
        <div className="mt-10 flex flex-col items-center gap-3">
          <div
            className="h-10 w-10 animate-spin rounded-full border-[3.5px] border-accent/25 border-t-accent"
            role="status"
            aria-label="Cargando..."
          />
        </div>
      </div>
    </aside>
  );
}
